import { z } from 'zod';

/**
 * Every error code the API can return. The client maps each one to a message in
 * `i18n/en.json` under `errors.<code>`, so adding a code here means adding a string there.
 * A unit test in the client asserts the two lists match.
 */
export const ERROR_CODES = [
  // auth and session
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'ACCESS_REQUIRED',
  'INVALID_TICKET',
  'INVALID_STATE',
  'OAUTH_FAILED',
  // validation and generic
  'BAD_REQUEST',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'INTERNAL',
  // a service we depend on
  'PROVIDER_ERROR',
  'DISCORD_ERROR',
  'NOT_CONFIGURED',
  // crew
  'USER_NOT_FOUND',
  'HAS_OWN_MEMBERSHIP',
  'ALREADY_IN_CREW',
  'CANNOT_INVITE_SELF',
  'NO_FREE_SEAT',
  'SEAT_ON_COOLDOWN',
  'INVITE_EXPIRED',
  'NOT_CREW_OWNER',
  'NOT_CREW_GUEST',
  'SEAT_EMPTY',
  // membership
  'ALREADY_SUBSCRIBED',
  'NO_MEMBERSHIP',
  'SAME_PLAN',
  // buying a game outright
  // builds and downloads
  'BUILD_SUPERSEDED',
  'CHUNK_NOT_IN_BUILD',
  'BUILD_NOT_READY',
  'NO_CURRENT_BUILD',
  'BATCH_TOO_LARGE',
  'INVALID_MANIFEST',
  // access passes
  'INVALID_PASS',
  // roles and creators
  'LAST_ADMIN',
  'ALREADY_CREATOR',
  'BILLING_NOT_CONFIGURED',
  'BILLING_UNAVAILABLE',
  'BILLING_FAILED',
] as const;

export const errorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

export const apiErrorSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    details: z.record(z.unknown()).optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

/** Default HTTP status for each code, used by the API's error helper. */
export const ERROR_STATUS: Record<ErrorCode, number> = {
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  ACCESS_REQUIRED: 403,
  INVALID_TICKET: 400,
  INVALID_STATE: 400,
  OAUTH_FAILED: 400,
  BAD_REQUEST: 400,
  NOT_FOUND: 404,
  CONFLICT: 409,
  RATE_LIMITED: 429,
  INTERNAL: 500,
  PROVIDER_ERROR: 502,
  DISCORD_ERROR: 502,
  NOT_CONFIGURED: 501,
  USER_NOT_FOUND: 404,
  HAS_OWN_MEMBERSHIP: 409,
  ALREADY_IN_CREW: 409,
  CANNOT_INVITE_SELF: 400,
  NO_FREE_SEAT: 409,
  SEAT_ON_COOLDOWN: 409,
  INVITE_EXPIRED: 410,
  NOT_CREW_OWNER: 403,
  NOT_CREW_GUEST: 403,
  SEAT_EMPTY: 404,
  ALREADY_SUBSCRIBED: 409,
  NO_MEMBERSHIP: 404,
  SAME_PLAN: 409,
  BUILD_SUPERSEDED: 409,
  CHUNK_NOT_IN_BUILD: 400,
  BUILD_NOT_READY: 409,
  NO_CURRENT_BUILD: 404,
  BATCH_TOO_LARGE: 400,
  INVALID_MANIFEST: 400,
  INVALID_PASS: 403,
  LAST_ADMIN: 409,
  ALREADY_CREATOR: 409,
  BILLING_NOT_CONFIGURED: 503,
  BILLING_UNAVAILABLE: 502,
  BILLING_FAILED: 402,
};
